// After a PDP analysis the API rebuilds the page's markup in the
// background (page videos re-detected, each link's VideoObject and the
// page markup regenerated — a Gemini call, so it lands seconds after
// the score returns). The rescore response carries only the score, so a
// page that just showed the old markup keeps showing it until something
// refetches. This watch polls the list until the row's markup differs from
// what it was before the analysis, then hands the fresh rows back. Silent
// on the deadline: an analysis that found the same videos regenerates
// nothing, which is not an error.

interface MarkupRow {
    id: number;
    videos?: Array<{ s3_file_id: number; json_ld?: any }> | null;
    page_videos?: any[] | null;
    product_json_ld?: any;
}

// Everything the analysis can rewrite on a row, as one comparable string.
export const markupSignature = (row: MarkupRow | null | undefined): string =>
    JSON.stringify(
        row
            ? {
                  videos: (row.videos || []).map((l) => [l.s3_file_id, l.json_ld ?? null]),
                  page_videos: row.page_videos ?? null,
                  product_json_ld: row.product_json_ld ?? null
              }
            : null
    );

interface WatchOptions<R extends MarkupRow> {
    rowId: number;
    prev: string;
    // The list as the server has it now; null on a transient failure.
    load: () => Promise<R[] | null>;
    onChange: (rows: R[]) => void;
    intervalMs?: number;
    timeoutMs?: number;
}

// Returns a cancel function for unmount.
export function watchMarkupChange<R extends MarkupRow>({
    rowId,
    prev,
    load,
    onChange,
    intervalMs = 4000,
    timeoutMs = 90000
}: WatchOptions<R>): () => void {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const started = Date.now();
    const tick = async () => {
        if (cancelled) return;
        const rows = await load();
        if (cancelled) return;
        const row = rows?.find((r) => r.id === rowId);
        if (rows && row && markupSignature(row) !== prev) {
            onChange(rows);
            return;
        }
        if (Date.now() - started > timeoutMs) return;
        timer = setTimeout(tick, intervalMs);
    };
    timer = setTimeout(tick, intervalMs);
    return () => {
        cancelled = true;
        if (timer) clearTimeout(timer);
    };
}
